/**
 * Draws the simulated scene: every active SpriteRenderer / TilemapRenderer sorted by sorting layer
 * and order (creation order breaks ties), dash trails, with the camera from CameraController.
 */
import { applyCamera, clipToMask, drawScene, drawableItem, tintedImage, viewRect, TileLayerView, type Camera, type SceneItem } from '../../render/renderer.ts';
import { sortKey, spriteFromRef, type SpriteRef } from '../../data/gameData.ts';
import type { Xform } from '../../model/xform.ts';
import type { Game } from '../sim/game.ts';
import type { SimGo, SpriteState } from '../sim/world.ts';
import { PlayerController, PlayerState } from '../sim/player.ts';
import { doorMaskFor, type DoorMask } from '../sim/mechanics/level.ts';
import { KOTHHandler, VolleyballHandler } from '../sim/mechanics/modes.ts';
import { FIXED_DT, type RGBA, type V2 } from '../sim/unity.ts';

/** DashTrailSprite prefab values */
const TRAIL = { durationFrames: 15, fadeDurationFrames: 15, startingOpacity: 0.15, startingSortOrder: -1000, parts: 4 };
const EXCLUDE_EPS = 1 / 255 + 1e-4;

export class GameRenderer {
  private tileViews = new Map<string, TileLayerView>();
  private offsets = new Map<SimGo, V2 | null>();

  /** draw into the w x h viewport whose top-left corner is (ox, oy) in canvas pixels */
  render(ctx: CanvasRenderingContext2D, game: Game, cam: Camera, w: number, h: number, ox = 0, oy = 0) {
    const bg = game.doc.settings.backgroundColor ?? [0.2, 0.2, 0.2, 1];
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.beginPath();
    ctx.rect(ox, oy, w, h);
    ctx.clip();
    ctx.fillStyle = `rgb(${bg[0] * 255},${bg[1] * 255},${bg[2] * 255})`;
    ctx.fillRect(ox, oy, w, h);
    applyCamera(ctx, cam, w, h);
    if (ox || oy) {
      const m = ctx.getTransform();
      ctx.setTransform(m.a, m.b, m.c, m.d, m.e + ox, m.f + oy);
    }
    ctx.imageSmoothingEnabled = false;
    const view = viewRect(cam, w, h);
    const items: SceneItem[] = [];
    this.offsets.clear();

    for (const g of game.gos) {
      const s = g.sprite, t = g.tilemap;
      const hasSprite = !!(s && s.enabled && s.sprite);
      const hasSquare = !!(s && s.enabled && !s.sprite && s.square && s.color[3] > 0);
      const hasTiles = !!(t && t.enabled && t.tiles.length);
      if ((!hasSprite && !hasTiles && !hasSquare) || g.destroyed || !g.active) continue;
      let m = g.worldXform();
      const off = this.offsetOf(g);
      if (off) m = { ...m, tx: m.tx + off.x, ty: m.ty + off.y };
      if (hasSquare) pushSquare(items, s!, m);
      if (hasSprite) this.pushVisible(items, g, s!, m, view);
      if (hasTiles) {
        items.push(drawableItem({ kind: 'tilemap', tiles: t!.tiles, palette: t!.palette, world: m, color: t!.color, sortingLayer: t!.sortingLayer, order: t!.order, go: null as never, key: t!.key }, view, this.tileViews));
      }
    }
    for (const p of game.players as PlayerController[]) this.pushTrail(items, game, p);
    drawScene(ctx, items);
    drawModeLabels(ctx, game);
    ctx.restore();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
  }

  /** accumulated cosmetic offsets (Oscillator) of a GameObject and its ancestors */
  private offsetOf(g: SimGo): V2 | null {
    if (this.offsets.has(g)) return this.offsets.get(g)!;
    const p = g.parent ? this.offsetOf(g.parent) : null;
    const o = g.renderOffset;
    const r = o && p ? { x: o.x + p.x, y: o.y + p.y } : o ?? p;
    this.offsets.set(g, r);
    return r;
  }

  /** Door mask wins over a sprite mask. Otherwise VisibleInside/Outside sprites clip to their hierarchy SpriteMask. */
  private pushVisible(items: SceneItem[], g: SimGo, s: SpriteState, m: Xform, view: [number, number, number, number]) {
    const door = playerInDoor(g);
    if (door) {
      this.pushClipped(items, s, m, view, (ctx) => drawDoorMask(ctx, door), true);
      return;
    }
    const mode = s.maskInteraction ?? 0;
    const host = mode === 1 || mode === 2 ? spriteMaskHost(g) : null;
    const maskSprite = host ? spriteFromRef(host.comps.get('SpriteMask')?.m_Sprite) : null;
    if (host && maskSprite) {
      const maskM = this.placed(host);
      const paint = (ctx: CanvasRenderingContext2D, at: Xform) => drawSprite(ctx, at, maskSprite, tintedImage(maskSprite.tex, [1, 1, 1, 1]), 1, false, false);
      this.pushClipped(items, s, m, view, (ctx) => paint(ctx, maskM), mode === 1);
      if (g.renderCopies) {
        for (const c of g.renderCopies) {
          const shift = { x: c.x, y: c.y };
          this.pushClipped(items, s, moved(m, shift), view, (ctx) => paint(ctx, moved(maskM, shift)), mode === 1);
        }
      }
      return;
    }
    this.pushSprite(items, s, m, view);
    if (g.renderCopies) for (const c of g.renderCopies) this.pushSprite(items, s, moved(m, c), view);
  }

  private placed(g: SimGo): Xform {
    let m = g.worldXform();
    const off = this.offsetOf(g);
    if (off) m = moved(m, off);
    return m;
  }

  private pushClipped(items: SceneItem[], s: SpriteState, m: Xform, view: [number, number, number, number], mask: (ctx: CanvasRenderingContext2D) => void, inside: boolean) {
    const before = items.length;
    this.pushSprite(items, s, m, view);
    const item = items[before];
    if (!item) return;
    const draw = item.draw;
    item.draw = (ctx) => clipToMask(ctx, draw, mask, inside);
  }

  private pushSprite(items: SceneItem[], s: SpriteState, m: Xform, view: [number, number, number, number]) {
    const sp = s.sprite!;
    const ppu = sp.texDef.ppu;
    const sw = s.drawMode ? s.size[0] : sp.def.r[2] / ppu, sh = s.drawMode ? s.size[1] : sp.def.r[3] / ppu;
    const rad = Math.hypot(sw, sh) * Math.max(Math.hypot(m.a, m.b), Math.hypot(m.c, m.d)) + 0.5;
    if (m.tx + rad < view[0] || m.tx - rad > view[2] || m.ty + rad < view[1] || m.ty - rad > view[3]) return;
    if (s.excludeTint && !s.drawMode) {
      const ex = s.excludeTint;
      const color = s.color;
      items.push({
        key: sortKey(s.sortingLayer, s.order),
        draw: (ctx) => drawSprite(ctx, m, sp, excludeTinted(sp, color, ex), color[3], s.flipX, s.flipY),
      });
      return;
    }
    items.push(drawableItem({ kind: 'sprite', sprite: sp, world: m, color: s.color, flipX: s.flipX, flipY: s.flipY, sortingLayer: s.sortingLayer, order: s.order, drawMode: s.drawMode, size: s.size, tileMode: s.tileMode, tileThreshold: s.tileThreshold, go: null as never }, view, this.tileViews));
  }

  /** PlayerController.SpawnDashTrail + DashTrailSprite (solid colour silhouettes) */
  private pushTrail(items: SceneItem[], game: Game, p: PlayerController) {
    if (!p.trail.length || !p.go.sprite) return;
    const layer = p.go.sprite.sortingLayer;
    const color = p.go.sprite.color;
    let prev: { x: number; y: number; f: number } | null = null;
    let order = TRAIL.startingSortOrder;
    let inChain = false;
    for (const e of p.trail) {
      const f = Math.round(e.t / FIXED_DT);
      const chained = !!prev && f - prev.f === 1;
      if (!chained) inChain = false;
      const parts: V2[] = [];
      if (chained) for (let i = 1; i < TRAIL.parts; i++) parts.push({ x: (prev!.x - e.x) * (i / TRAIL.parts) + e.x, y: (prev!.y - e.y) * (i / TRAIL.parts) + e.y });
      parts.push({ x: e.x, y: e.y });
      prev = { x: e.x, y: e.y, f };
      const age = game.frame - f;
      const fade = age - TRAIL.durationFrames;
      const opacity = fade > 0 ? 1 - fade / TRAIL.fadeDurationFrames : 1;
      for (const pt of parts) {
        order = inChain ? order - 1 : TRAIL.startingSortOrder;
        inChain = true;
        const o = order;
        if (opacity <= 0 || !e.sprite) continue;
        const sprite = e.sprite;
        const m: Xform = { a: e.scale, b: 0, c: 0, d: e.scale, tx: pt.x, ty: pt.y };
        const a = opacity * TRAIL.startingOpacity;
        items.push({ key: sortKey(layer, o), draw: (ctx) => drawSprite(ctx, m, sprite, solidColor(sprite, color), a, false, false) });
      }
    }
  }
}

function playerInDoor(g: SimGo): DoorMask | null {
  const player = g.getBehaviour(PlayerController);
  if (!player || player.playerState !== PlayerState.InDoor) return null;
  return doorMaskFor(player);
}

function moved(m: Xform, by: V2): Xform {
  return { ...m, tx: m.tx + by.x, ty: m.ty + by.y };
}

/** SpriteMask on this object or under an ancestor. The mask object keeps its own transform (the ball stays upright). */
function spriteMaskHost(start: SimGo): SimGo | null {
  let cur: SimGo | null = start;
  while (cur) {
    const hit = spriteMaskUnder(cur);
    if (hit) return hit;
    cur = cur.parent;
  }
  return null;
}

function spriteMaskUnder(g: SimGo): SimGo | null {
  if (g.destroyed || !g.active) return null;
  const d = g.comps.get('SpriteMask');
  if (d && String(d.m_Enabled ?? '1') !== '0' && spriteFromRef(d.m_Sprite)) return g;
  for (const c of g.children) {
    const hit = spriteMaskUnder(c);
    if (hit) return hit;
  }
  return null;
}

function drawDoorMask(ctx: CanvasRenderingContext2D, mask: DoorMask) {
  if (mask.kind === 'rect') {
    ctx.save();
    ctx.fillStyle = '#fff';
    ctx.fillRect(mask.x, mask.y, mask.w, mask.h);
    ctx.restore();
    return;
  }
  drawSprite(ctx, mask.m, mask.sprite, tintedImage(mask.sprite.tex, [1, 1, 1, 1]), 1, mask.flipX, mask.flipY);
}

function drawModeLabels(ctx: CanvasRenderingContext2D, game: Game) {
  for (const b of game.behaviours) {
    const labels = b instanceof KOTHHandler || b instanceof VolleyballHandler ? b.labels : null;
    if (!labels) continue;
    for (const lab of labels) drawWorldText(ctx, lab.x, lab.y, lab.text, lab.color, b instanceof KOTHHandler ? 0.45 : 0.7);
  }
}

function drawWorldText(ctx: CanvasRenderingContext2D, x: number, y: number, text: string, color: RGBA, size: number) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(1, -1);
  ctx.font = `700 ${size}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = `rgb(${color[0] * 255},${color[1] * 255},${color[2] * 255})`;
  ctx.fillText(text, 0, 0);
  ctx.restore();
}

function pushSquare(items: SceneItem[], s: SpriteState, m: Xform) {
  const [w, h] = s.drawMode ? s.size : [1, 1];
  const c = s.color;
  items.push({
    key: sortKey(s.sortingLayer, s.order),
    draw: (ctx) => {
      ctx.save();
      ctx.transform(m.a, m.b, m.c, m.d, m.tx, m.ty);
      ctx.globalAlpha *= c[3];
      ctx.fillStyle = `rgb(${c[0] * 255},${c[1] * 255},${c[2] * 255})`;
      ctx.fillRect(-w / 2, -h / 2, w, h);
      ctx.restore();
    },
  });
}

function drawSprite(ctx: CanvasRenderingContext2D, m: Xform, s: SpriteRef, img: CanvasImageSource | null, alpha: number, flipX: boolean, flipY: boolean) {
  if (!img) return;
  const ppu = s.texDef.ppu;
  const [, , rw, rh] = s.def.r;
  const w = rw / ppu, h = rh / ppu;
  const x0 = -s.def.p[0] * w, y0 = -s.def.p[1] * h;
  ctx.save();
  ctx.transform(m.a, m.b, m.c, m.d, m.tx, m.ty);
  if (flipX || flipY) ctx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
  ctx.scale(1, -1);
  ctx.globalAlpha *= alpha;
  ctx.drawImage(img, 0, 0, rw, rh, x0, -(y0 + h), w, h);
  ctx.restore();
}

const spriteCache = new Map<string, HTMLCanvasElement | null>();

/** the sprite's pixels in a canvas of its own, processed per pixel */
function processed(s: SpriteRef, key: string, fn: (d: Uint8ClampedArray) => void): HTMLCanvasElement | null {
  const k = `${s.tex}|${s.def.r.join(',')}|${key}`;
  if (spriteCache.has(k)) return spriteCache.get(k)!;
  const img = tintedImage(s.tex, [1, 1, 1, 1]);
  if (!img) return null;
  const [rx, ry, rw, rh] = s.def.r;
  const c = document.createElement('canvas');
  c.width = Math.max(1, rw);
  c.height = Math.max(1, rh);
  const g = c.getContext('2d', { willReadFrequently: true })!;
  g.drawImage(img, rx, s.texDef.h - ry - rh, rw, rh, 0, 0, rw, rh);
  const id = g.getImageData(0, 0, c.width, c.height);
  fn(id.data);
  g.putImageData(id, 0, 0);
  spriteCache.set(k, c);
  return c;
}

/** SpriteExcludeTint shader: pixels matching the exclude colour keep their texture colour */
function excludeTinted(s: SpriteRef, color: RGBA, ex: RGBA): HTMLCanvasElement | null {
  const key = `ex:${color.slice(0, 3).map((v) => v.toFixed(3)).join(',')}:${ex.slice(0, 3).map((v) => v.toFixed(4)).join(',')}`;
  return processed(s, key, (d) => {
    for (let i = 0; i < d.length; i += 4) {
      if (!d[i + 3]) continue;
      const r = d[i] / 255, g = d[i + 1] / 255, b = d[i + 2] / 255;
      if (Math.abs(r - ex[0]) <= EXCLUDE_EPS && Math.abs(g - ex[1]) <= EXCLUDE_EPS && Math.abs(b - ex[2]) <= EXCLUDE_EPS) continue;
      d[i] = r * color[0] * 255;
      d[i + 1] = g * color[1] * 255;
      d[i + 2] = b * color[2] * 255;
    }
  });
}

/** SpriteSolidColor material: the sprite's alpha filled with one colour */
function solidColor(s: SpriteRef, color: RGBA): HTMLCanvasElement | null {
  const key = `solid:${color.slice(0, 3).map((v) => v.toFixed(3)).join(',')}`;
  return processed(s, key, (d) => {
    for (let i = 0; i < d.length; i += 4) {
      d[i] = color[0] * 255;
      d[i + 1] = color[1] * 255;
      d[i + 2] = color[2] * 255;
    }
  });
}
